import { useCallback, useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react'

interface Props {
  label: string
  className: string
  children: ReactNode
}

const DRAG_THRESHOLD_PX = 5

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// Sideways scroller for the rung ladders. Adds what a bare overflow-x box lacks on a desktop with a mouse:
// prev/next buttons, edge fades that show more is hidden, and click-drag. Touch, trackpads and the arrow keys
// (the region is focusable) keep native scrolling. Buttons hide themselves when there is nothing to scroll.
export function HScroll({ label, className, children }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const drag = useRef<{ x: number; left: number; moved: boolean } | null>(null)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(true)
  const [dragging, setDragging] = useState(false)

  const update = useCallback(() => {
    const el = ref.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    setAtStart(el.scrollLeft <= 1)
    setAtEnd(el.scrollLeft >= max - 1)
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    update()
    el.addEventListener('scroll', update, { passive: true })
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null
    ro?.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      ro?.disconnect()
    }
  }, [update])

  const page = (dir: 1 | -1) => {
    const el = ref.current
    if (!el) return
    el.scrollBy({ left: dir * Math.max(200, el.clientWidth * 0.8), behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
  }

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse' || e.button !== 0 || !ref.current) return
    drag.current = { x: e.clientX, left: ref.current.scrollLeft, moved: false }
  }
  const endDrag = () => {
    drag.current = null
    setDragging(false)
  }
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current
    const el = ref.current
    if (!d || !el) return
    // Button released outside the region before the drag captured the pointer: the pointerup never reached us.
    if ((e.buttons & 1) === 0) {
      endDrag()
      return
    }
    const dx = e.clientX - d.x
    if (!d.moved && Math.abs(dx) < DRAG_THRESHOLD_PX) return
    if (!d.moved) {
      d.moved = true
      setDragging(true)
      el.setPointerCapture?.(e.pointerId)
    }
    el.scrollLeft = d.left - dx
  }

  return (
    <div className="strat-hs" data-at-start={atStart || undefined} data-at-end={atEnd || undefined}>
      <div
        ref={ref}
        className={`${className}${dragging ? ' is-dragging' : ''}`}
        tabIndex={0}
        role="region"
        aria-label={label}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
      >
        {children}
      </div>
      {/* Mouse affordances only: keyboard users scroll the focused region with the arrow keys. */}
      <button type="button" className="strat-hs-btn strat-hs-btn--prev" aria-hidden="true" tabIndex={-1}
        hidden={atStart} onClick={() => page(-1)}>‹</button>
      <button type="button" className="strat-hs-btn strat-hs-btn--next" aria-hidden="true" tabIndex={-1}
        hidden={atEnd} onClick={() => page(1)}>›</button>
    </div>
  )
}
